/**
 * CoverRenderer.js
 *
 * Renders a magazine cover with a realistic 3-D perspective tilt, simulating
 * a Y-axis rotation so one side of the cover appears closer to the viewer.
 *
 * The technique uses vertical-strip scaling: the cover image is divided into
 * narrow vertical strips.  Strips on the "far" side are drawn progressively
 * narrower and slightly taller (foreshortened), while strips on the "near"
 * side remain close to their original width.  A lighting gradient is overlaid
 * to brighten the near side and darken the far side, and a drop shadow is
 * cast beneath the cover.
 *
 * An optional thin white spine edge is drawn on the receding side to simulate
 * the thickness of the magazine.
 *
 * The rendered result is returned as an HTMLCanvasElement ready for Fabric.js.
 *
 * @module CoverRenderer
 */

/* ------------------------------------------------------------------ */
/*  Defaults                                                           */
/* ------------------------------------------------------------------ */

/** @type {CoverOptions} */
const DEFAULTS = {
  width: 500,
  height: 700,
  rotateY: -15,        // degrees — negative = left side recedes
  shadow: true,
  shadowBlur: 20,
  shadowOffsetX: 10,
  shadowOffsetY: 10,
  shadowColor: 'rgba(0, 0, 0, 0.50)',
  spineEdge: true,
  spineWidth: 6,       // pixels
  spineColor: '#e8e8e8',
  numStrips: 30,
  lightingEnabled: true,
};

/**
 * @typedef {object} CoverOptions
 * @property {number}  [width=500]
 * @property {number}  [height=700]
 * @property {number}  [rotateY=-15]       – degrees, negative = left recedes
 * @property {boolean} [shadow=true]
 * @property {number}  [shadowBlur=20]
 * @property {number}  [shadowOffsetX=10]
 * @property {number}  [shadowOffsetY=10]
 * @property {string}  [shadowColor]
 * @property {boolean} [spineEdge=true]
 * @property {number}  [spineWidth=6]
 * @property {string}  [spineColor='#e8e8e8']
 * @property {number}  [numStrips=30]
 * @property {boolean} [lightingEnabled=true]
 */

/* ------------------------------------------------------------------ */
/*  CoverRenderer class                                                */
/* ------------------------------------------------------------------ */

export class CoverRenderer {
  /**
   * @param {Partial<CoverOptions>} [defaults]
   */
  constructor(defaults = {}) {
    /** @type {CoverOptions} */
    this.defaults = { ...DEFAULTS, ...defaults };
  }

  /**
   * Render the magazine cover with 3-D perspective tilt.
   *
   * @param {HTMLImageElement|HTMLCanvasElement|ImageBitmap} coverImg
   * @param {Partial<CoverOptions>} [options]
   * @returns {HTMLCanvasElement}
   */
  render(coverImg, options = {}) {
    const opts = { ...this.defaults, ...options };
    const {
      width,
      height,
      rotateY,
      shadow,
      shadowBlur,
      shadowOffsetX,
      shadowOffsetY,
      shadowColor,
      spineEdge,
      spineWidth,
      spineColor,
      numStrips,
      lightingEnabled,
    } = opts;

    if (!coverImg) {
      throw new Error('CoverRenderer.render() requires a coverImg argument');
    }

    const imgW = coverImg.width || coverImg.naturalWidth || width;
    const imgH = coverImg.height || coverImg.naturalHeight || height;

    // Canvas sizing — add margin for shadow and any height expansion
    const margin = shadow ? shadowBlur * 2 + Math.max(Math.abs(shadowOffsetX), Math.abs(shadowOffsetY)) : 20;
    const canvas = document.createElement('canvas');
    canvas.width = width + margin * 2 + (spineEdge ? spineWidth : 0);
    canvas.height = height + margin * 2;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // -------- Compute per-strip geometry --------
    const angleNorm = Math.min(Math.abs(rotateY), 80) / 80; // 0-1 normalised
    const leftRecedes = rotateY < 0;

    // Build strip widths — far-side strips are narrower
    const stripWidths = new Array(numStrips);
    let totalW = 0;
    for (let i = 0; i < numStrips; i++) {
      // t: 0 = left edge, 1 = right edge
      const t = i / (numStrips - 1);
      // distFromFar: 0 at the far edge, 1 at the near edge
      const distFromFar = leftRecedes ? t : (1 - t);

      // Width multiplier: 1 at near edge, shrinks toward far edge
      // Using a cosine curve for smooth falloff
      const shrink = 1 - angleNorm * 0.55 * (1 - distFromFar);
      stripWidths[i] = Math.max(shrink, 0.35);
      totalW += stripWidths[i];
    }
    // Normalise so total matches target width
    const scale = width / totalW;
    for (let i = 0; i < numStrips; i++) stripWidths[i] *= scale;

    // Per-strip height scaling — far side slightly taller (perspective)
    const stripHeights = new Array(numStrips);
    for (let i = 0; i < numStrips; i++) {
      const t = i / (numStrips - 1);
      const distFromFar = leftRecedes ? t : (1 - t);
      // Height: near side = 1, far side slightly shrinks
      const hScale = 1 - angleNorm * 0.10 * (1 - distFromFar);
      stripHeights[i] = height * hScale;
    }

    // -------- Draw drop shadow --------
    // We need the actual bounding box of the warped cover first
    const offsetX = margin + (spineEdge && leftRecedes ? spineWidth : 0);
    const offsetY = margin;

    if (shadow) {
      ctx.save();
      ctx.shadowColor = shadowColor;
      ctx.shadowBlur = shadowBlur;
      ctx.shadowOffsetX = shadowOffsetX;
      ctx.shadowOffsetY = shadowOffsetY;

      // Draw a shape that approximates the warped cover outline for shadow
      ctx.beginPath();
      let sx = offsetX;
      // Top edge
      for (let i = 0; i < numStrips; i++) {
        const topY = offsetY + (height - stripHeights[i]) / 2;
        if (i === 0) ctx.moveTo(sx, topY);
        else ctx.lineTo(sx, topY);
        sx += stripWidths[i];
      }
      // Right edge
      const lastH = stripHeights[numStrips - 1];
      const lastTopY = offsetY + (height - lastH) / 2;
      ctx.lineTo(sx, lastTopY + lastH);
      // Bottom edge (reverse)
      sx = offsetX;
      let bottomPts = [];
      for (let i = 0; i < numStrips; i++) {
        const botY = offsetY + (height - stripHeights[i]) / 2 + stripHeights[i];
        bottomPts.push({ x: sx, y: botY });
        sx += stripWidths[i];
      }
      for (let i = bottomPts.length - 1; i >= 0; i--) {
        ctx.lineTo(bottomPts[i].x, bottomPts[i].y);
      }
      ctx.closePath();
      ctx.fillStyle = '#fff';
      ctx.fill();
      ctx.restore();
    }

    // -------- Draw spine edge --------
    if (spineEdge && angleNorm > 0.02) {
      const spineX = leftRecedes ? offsetX - spineWidth : offsetX + width;
      const spineTopIdx = leftRecedes ? 0 : numStrips - 1;
      const spineH = stripHeights[spineTopIdx];
      const spineTopY = offsetY + (height - spineH) / 2;

      ctx.save();
      // Gradient from light to slightly darker to simulate spine rounding
      const grad = ctx.createLinearGradient(spineX, 0, spineX + spineWidth, 0);
      if (leftRecedes) {
        grad.addColorStop(0, spineColor);
        grad.addColorStop(0.5, this._darkenColor(spineColor, 0.15));
        grad.addColorStop(1, this._darkenColor(spineColor, 0.05));
      } else {
        grad.addColorStop(0, this._darkenColor(spineColor, 0.05));
        grad.addColorStop(0.5, this._darkenColor(spineColor, 0.15));
        grad.addColorStop(1, spineColor);
      }
      ctx.fillStyle = grad;
      ctx.fillRect(spineX, spineTopY, spineWidth, spineH);

      // Thin dark edge line
      ctx.fillStyle = 'rgba(0,0,0,0.15)';
      const edgeX = leftRecedes ? spineX : spineX + spineWidth - 1;
      ctx.fillRect(edgeX, spineTopY, 1, spineH);
      ctx.restore();
    }

    // -------- Draw warped cover strips --------
    const srcStripW = imgW / numStrips;
    let dx = offsetX;

    ctx.save();
    for (let i = 0; i < numStrips; i++) {
      const dw = stripWidths[i];
      const dh = stripHeights[i];
      const dy = offsetY + (height - dh) / 2;

      // Source strip
      const sx = i * srcStripW;
      const sw = srcStripW + 0.5;

      ctx.save();
      // Clip to prevent sub-pixel bleed between strips
      ctx.beginPath();
      ctx.rect(dx - 0.5, dy - 0.5, dw + 1, dh + 1);
      ctx.clip();

      ctx.drawImage(coverImg, sx, 0, sw, imgH, dx, dy, dw + 0.5, dh);

      // ---- Lighting ----
      if (lightingEnabled && angleNorm > 0.02) {
        const t = i / (numStrips - 1);
        const distFromFar = leftRecedes ? t : (1 - t);

        // Far side: darken — Near side: slight highlight
        if (distFromFar < 0.5) {
          // Far half — shadow overlay
          const shadowAlpha = (1 - distFromFar * 2) * 0.25 * angleNorm;
          ctx.fillStyle = `rgba(0, 0, 0, ${shadowAlpha.toFixed(4)})`;
          ctx.fillRect(dx - 0.5, dy - 0.5, dw + 1, dh + 1);
        } else {
          // Near half — subtle highlight
          const highlightAlpha = (distFromFar - 0.5) * 2 * 0.08 * angleNorm;
          ctx.fillStyle = `rgba(255, 255, 255, ${highlightAlpha.toFixed(4)})`;
          ctx.fillRect(dx - 0.5, dy - 0.5, dw + 1, dh + 1);
        }
      }

      ctx.restore();
      dx += dw;
    }
    ctx.restore();

    return canvas;
  }

  /* ---------------------------------------------------------------- */
  /*  Private helpers                                                   */
  /* ---------------------------------------------------------------- */

  /**
   * Darken a CSS colour string by a given factor.
   * Works with hex colours (#rrggbb) and named CSS greys.
   *
   * @private
   * @param {string} color
   * @param {number} amount – 0-1, how much to darken
   * @returns {string} – hex colour
   */
  _darkenColor(color, amount) {
    // Parse hex
    let hex = color.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
    const r = Math.max(0, parseInt(hex.substring(0, 2), 16) - Math.round(255 * amount));
    const g = Math.max(0, parseInt(hex.substring(2, 4), 16) - Math.round(255 * amount));
    const b = Math.max(0, parseInt(hex.substring(4, 6), 16) - Math.round(255 * amount));
    return `rgb(${r}, ${g}, ${b})`;
  }
}
